import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import AuthService from '../services/AuthService';
import { connectSocket } from '../services/socket';
import { isPathAllowed, TIER_HOME } from '../config/pageAccess';
import { isSessionExpired, endSession } from '../utils/session';

// Route guard: unauthenticated users are sent to /login and returned
// to their original destination after signing in. Authenticated users whose access
// tier can't see the current page are redirected to their tier's home page.
export default function RequireAuth({ children }) {
  const location = useLocation();

  // AuthService.login() connects the socket on a fresh sign-in, but a page reload
  // skips that — the token's already in localStorage, so bootstrap the connection
  // here too. connectSocket() is a no-op if already connected.
  useEffect(() => {
    if (AuthService.isAuthenticated()) connectSocket();
  }, []);

  // Being made an incident's team leader can happen after sign-in, so re-pull the
  // stored user's leadsIncident flag on each navigation — it opens the Team Leader
  // console (see pageAccess.js) — and re-render once it's in.
  const [, setUserVersion] = useState(0);
  useEffect(() => {
    if (!AuthService.isAuthenticated()) return;
    AuthService.refreshUser().then((changed) => changed && setUserVersion((v) => v + 1)).catch(() => {});
  }, [location.pathname]);

  // Web sessions end after a fixed 12 hours. Catch it even on a page left open with no
  // requests going out — every request is also checked in httpClient.
  useEffect(() => {
    const id = setInterval(() => {
      if (isSessionExpired()) endSession();
    }, 30000);
    return () => clearInterval(id);
  }, []);

  if (isSessionExpired()) {
    endSession();
    return null;
  }

  if (!AuthService.isAuthenticated()) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  const user = AuthService.getUser();
  const tier = user?.accessTier;
  if (!isPathAllowed(location.pathname, tier, user)) {
    return <Navigate to={TIER_HOME[tier] || '/dashboard'} replace />;
  }
  return children;
}
